/**
 * Safely parses a single row string into an array of cell values.
 * Handles quoted fields, escaped quotes (""), and ignores delimiters inside quotes.
 */
export function parseRow(row: string, columnSeparator: string): string[] {
  const cells: string[] = [];
  let currentCell = '';
  let inQuotes = false;

  // Safely clean trailing \r (from Windows \r\n line endings)
  // without destroying \r characters inside quoted cells.
  if (row.endsWith('\r')) {
    row = row.slice(0, -1);
  }

  for (let i = 0; i < row.length; i++) {
    const char = row[i];

    if (char === '"') {
      // Check for escaped quotes ("") inside a quoted string
      if (inQuotes && i + 1 < row.length && row[i + 1] === '"') {
        currentCell += '"';
        i++; // Skip the next quote since we just consumed it
      } else {
        inQuotes = !inQuotes; // Toggle quote state
      }
    } else if (char === columnSeparator && !inQuotes) {
      // We hit a separator outside of quotes -> end of cell
      cells.push(currentCell);
      currentCell = '';
    } else {
      // Normal character, just append it
      currentCell += char;
    }
  }

  // Push the final cell after the loop ends
  cells.push(currentCell);

  return cells;
}

export function linesToRows(lines: string[], columnSeparator: string) {
  const rows = [];
  for (const line of lines) {
    if (line.length > 0) {
      const cleanedLine = line.replace('\r', ''); // Some tables have \r\n line endings
      const cells = cleanedLine.split(columnSeparator);
      rows.push(cells);
    }
  }
  return rows;
}

export function getHeaders(line: string, columnSeparator: string): string[] {
  if (!line || line.length === 0) {
    return [];
  }
  return parseRow(line, columnSeparator);
}

export function linesToColumns(lines: string[], columnData: string[][], columnSeparator: string): void {
  for (const line of lines) {
    if (line.length === 0) continue;

    const cells = parseRow(line, columnSeparator);

    cells.forEach((cellData, index) => {
      // Safety check: ensure we don't crash if a row has MORE
      // columns than the header row originally defined.
      if (columnData[index]) {
        columnData[index].push(cellData);
      }
    });
  }
}

/**
 * Fetch and process a stream containing a TSV file.
 *
 * Returns an object containing the header names as keys and the column data as values.
 */
export async function parseTsvFromStream(
  fetchUrl: string,
  columnSeparator: string = '\t',
  lineSeparator: string = '\n',
) {
  const response = await fetch(fetchUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch TSV from "${fetchUrl}": ${response.status} ${response.statusText}`);
  }
  if (response.body === null) {
    return { headers: [], columns: [] };
  }

  return await parseTsvStream(response.body, columnSeparator, lineSeparator);
}

/**
 * Core parsing logic. Accepts any ReadableStream.
 */
export async function parseTsvStream(
  stream: ReadableStream<any>,
  columnSeparator: string = '\t',
  lineSeparator: string = '\n',
) {
  let headers: string[] = [];
  const columns: string[][] = [];

  const reader = stream.getReader();
  const decoder = new TextDecoder('utf-8');

  let buffer = '';
  let fileEnded = false;
  let inQuotes = false;

  while (!fileEnded) {
    const { done, value } = await reader.read();
    if (done) {
      fileEnded = true;
      buffer += decoder.decode();
      // If the file doesn't end with a newline,
      // the very last row is still in the buffer. We must parse it.
      if (buffer.length > 0) {
        if (headers.length === 0) {
          // Edge case: the file was only one line long
          headers = getHeaders(buffer, columnSeparator);
          headers.forEach(() => columns.push([]));
        } else {
          linesToColumns([buffer], columns, columnSeparator);
        }
      }
    } else {
      const scannedLength = buffer.length;
      buffer += decoder.decode(value, { stream: true });
      const fullLines: string[] = [];
      let i = scannedLength;
      let lastLineEnd = 0;

      // Handle cells with quotes that may contain line separators.
      // We want to split on line separators that are not inside quotes.
      while (i < buffer.length) {
        if (buffer[i] === '"') {
          inQuotes = !inQuotes; // Toggle quote state
        } else if (buffer[i] === lineSeparator && !inQuotes) {
          // We found a lineSeparator AND we are not inside quotes!
          fullLines.push(buffer.slice(lastLineEnd, i));
          lastLineEnd = i + 1; // Skip the lineSeparator itself
        }
        i++;
      }

      // Remove the processed lines from the buffer
      buffer = buffer.slice(lastLineEnd);

      // Process the extracted lines.
      if (headers.length === 0 && fullLines.length > 0) {
        const headerLine = fullLines.shift();
        headers = getHeaders(headerLine ?? '', columnSeparator);
        headers.forEach((_) => columns.push([]));
      }

      if (fullLines.length > 0) {
        linesToColumns(fullLines, columns, columnSeparator);
      }
    }
  }
  return { headers, columns };
}

/**
 * Fetch and process a stream containing a TSV file.
 *
 * Returns an object containing the header names as keys and the column data as values.
 */
export async function parseTsv(fetchUrl: string, columnSeparator: string = '\t', lineSeparator: string = '\n') {
  const response = await fetch(fetchUrl);
  const sourceText = await response.text();
  const lines = sourceText.split(lineSeparator);
  const rows = linesToRows(lines, columnSeparator);
  const columnIds: string[] = rows.shift() || [];
  const rowCount = rows.length;
  const columns: string[][] = [];
  let colIdx = 0;
  for (const headerId of columnIds) {
    const col = [];
    for (let i = 0; i < rowCount; i++) {
      const val: any = rows[i][colIdx];
      col.push(val);
    }
    columns.push(col);
    colIdx++;
  }

  return { headers: columnIds, columns: columns };
}
